// PF_Exposure.jsx

import React, { useEffect, useState } from "react";
import api from '../functions/api';
import { useSelector } from 'react-redux';
import { Table, Collapse, Tag, Spin, Alert } from 'antd';
import ThreeTablesModal from "./SearchResultModal";
import getUserId from '../functions/getUserId';
import Piecharts_MUI from "./charts/Piecharts_MUI";
import RiskCategory from "./Risk_Category";
import InfoTooltip from "./InfoTooltip";  // DAS-2538/2537: (i) explanations
import "../css/PF_Analysis.css";

const { Panel } = Collapse;

const PF_Exposure = () => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedTicker, setSelectedTicker] = useState(null);

  const portfolioData = useSelector(state => state?.portfolio.data);
  const userId = getUserId();
  const demoMode = useSelector(state => state.demoMode.isDemoMode);
  const maybeBlur = (text) => demoMode ? <span className="blur-text">{text}</span> : text;

  const [exposureDataFiltered, setExposureDataFiltered] = useState(null);
  const [exposureDataTotal, setExposureDataTotal] = useState(null);
  const [riskData, setRiskData] = useState(null);
  const [concentration, setConcentration] = useState(null);  // DAS-2538
  const [drawdown, setDrawdown] = useState(null);  // DAS-2537
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!userId) {
      console.warn("userId not ready, skipping exposure fetch");
      return;
    }

    const fetchData = async () => {
      try {
        const [exposureRes, riskRes] = await Promise.all([
          api.get(`/pf_other_exposures`, { params: { userid: userId } }),
          api.get(`/pf_risk`, { params: { userid: userId } }),
        ]);

        // console.log("Exposure Data", exposureRes.data.portfolio_various_exposures_filtered, exposureRes.data.portfolio_various_exposures_total);
        // console.log("Risk Data", riskRes.data.portfolio_risk);

        setExposureDataFiltered(exposureRes.data.portfolio_various_exposures_filtered);
        setExposureDataTotal(exposureRes.data.portfolio_various_exposures_total);
        setRiskData(riskRes.data.portfolio_risk);
        setConcentration(riskRes.data.concentration);  // DAS-2538
        setDrawdown(riskRes.data.drawdown);  // DAS-2537
      } catch (err) {
        console.error("Error fetching exposure or risk data", err);
        setError("Failed to fetch exposure or risk data.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading) {
    return <Spin tip="Loading risk and exposure data..." />;
  }

  if (error) {
    return <Alert message={error} type="error" />;
  }

  if (!portfolioData || !portfolioData.exposures || portfolioData.exposures.length === 0) {
    return <div>No data available</div>;
  }

  const safeParseFloat = (str) => {
    const value = parseFloat(str);
    return isNaN(value) ? 0 : value;
  };

  const columns = [
    {
      title: 'Ticker',
      dataIndex: 'ticker',
      key: 'ticker',
      render: (text, record) => (
        <div>
          <a onClick={() => {
            setSelectedTicker(record.ticker);
            setIsModalVisible(true);
          }}>
            <Tag color="blue">{text}</Tag>
          </a>
          <div style={{ fontSize: '10px', color: '#888' }}>
            {record.company || ''}
          </div>
          {record.Assettype && (
            <div style={{ fontSize: '10px', fontStyle: 'italic', color: '#888' }}>
              {record.Assettype}
            </div>
          )}
        </div>
      )
    },
    {
      title: 'Part of Total Portfolio',
      dataIndex: 'total_amount_exposure_perc',
      key: 'total_amount_exposure_perc',
      sorter: (a, b) => parseFloat(a.total_amount_exposure_perc) - parseFloat(b.total_amount_exposure_perc),
      render: (text, record) => (
        <div>
          {`${parseFloat(text).toFixed(2)}%`}
          {record.part_underlying_assets_unknown !== null && (
            <div style={{ fontSize: '10px', color: '#888' }}>
              Unknown holdings part
            </div>
          )}
        </div>
      )
    },
    {
      title: 'Total Amount Exposure',
      dataIndex: 'total_amount_exposure',
      key: 'total_amount_exposure',
      sorter: (a, b) => parseFloat(a.total_amount_exposure) - parseFloat(b.total_amount_exposure),
      render: (text, record) => (
        <div>
          {maybeBlur(text)}
          {record.part_underlying_assets_unknown !== null && (
            <div style={{ fontSize: '10px', color: '#888' }}>
              Unknown holdings part
            </div>
          )}
        </div>
      )
    },
    {
      title: 'Tracker Exposure Amount',
      dataIndex: 'tracker_exposure_amount',
      key: 'tracker_exposure_amount',
      sorter: (a, b) => parseFloat(a.tracker_exposure_amount) - parseFloat(b.tracker_exposure_amount),
      render: (text, record) => {
        const details = record.tracker_exposure ? record.tracker_exposure.split(', ').map((detail, index) => (
          <div key={index}>{maybeBlur(detail)}</div>
        )) : null;

        return (
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <div>{maybeBlur(record.tracker_exposure_amount)}</div>
            <div style={{ display: 'flex', flexDirection: 'column', textAlign: 'right', fontSize: 10 }}>
              {details}
            </div>
          </div>
        );
      },
    },
    {
      title: 'Stock Exposure',
      dataIndex: 'stock_exposure',
      key: 'stock_exposure',
      sorter: (a, b) => safeParseFloat(a.stock_exposure) - safeParseFloat(b.stock_exposure),
      render: (text) => maybeBlur(text)
    },
  ];

  const dataSource = portfolioData.exposures.map((exposure, index) => ({
    key: index,
    ticker: exposure.ticker,
    company: exposure.company,
    Assettype: exposure.Assettype,
    total_amount_exposure: exposure.total_amount_exposure,
    tracker_exposure_amount: exposure.tracker_exposure_amount,
    tracker_exposure: exposure.tracker_exposure,
    stock_exposure: exposure.stock_exposure,
    total_amount_exposure_perc: exposure.total_amount_exposure_perc,
    part_underlying_assets_unknown: exposure.part_underlying_assets_unknown
  }));

  // Helper functions for portfolio-based pie charts (from PF_Piecharts_Analysis)
  const makeData = (arr, valueKey) => {
    const raw = arr.map((item) => parseFloat(item[valueKey]) || 0);
    const total = raw.reduce((a, b) => a + b, 0) || 1;
    const obj = {};
    raw.forEach((val, idx) => {
      if (val > 0) obj[idx] = { percent: (val / total) * 100 };
    });
    return { 0: obj };
  };

  const makeLegend = (arr, labelKey) =>
    Object.fromEntries(
      arr.map((item, idx) => [idx, item[labelKey] ?? "undefined"])
    );

  const renderExposurePieCharts = (data, titleSuffix) => (
    <>
      <Piecharts_MUI
        data={{ 0: Object.fromEntries(data.region.map((item, idx) => [idx, { percent: item.exposure_perc }])) }}
        title={`Region Exposure ${titleSuffix}`}
        labelPrefix="Region"
        legendInformation={Object.fromEntries(data.region.map((item, idx) => [idx, item.name]))}
        legendFontSize={12}
      />

      <Piecharts_MUI
        data={{ 0: Object.fromEntries(data.base_currency.map((item, idx) => [idx, { percent: item.exposure_perc }])) }}
        title={`Currency Exposure ${titleSuffix}`}
        labelPrefix="Currency"
        legendInformation={Object.fromEntries(data.base_currency.map((item, idx) => [idx, item.name]))}
        legendFontSize={12}
      />

      <Piecharts_MUI
        data={{ 0: Object.fromEntries(data.country.map((item, idx) => [idx, { percent: item.exposure_perc }])) }}
        title={`Country Exposure ${titleSuffix}`}
        labelPrefix="Country"
        legendInformation={Object.fromEntries(data.country.map((item, idx) => [idx, item.name]))}
        legendFontSize={12}
      />

      <Piecharts_MUI
        data={{ 0: Object.fromEntries(data.industry.map((item, idx) => [idx, { percent: item.exposure_perc }])) }}
        title={`Industry Exposure ${titleSuffix}`}
        labelPrefix="Industry"
        legendInformation={Object.fromEntries(data.industry.map((item, idx) => [idx, item.name]))}
        legendFontSize={12}
      />

      <Piecharts_MUI
        data={{ 0: Object.fromEntries(data.sector.map((item, idx) => [idx, { percent: item.exposure_perc }])) }}
        title={`Sector Exposure ${titleSuffix}`}
        labelPrefix="Sector"
        legendInformation={Object.fromEntries(data.sector.map((item, idx) => [idx, item.name]))}
        legendFontSize={12}
      />

      <Piecharts_MUI
        data={{ 0: Object.fromEntries(data.Assettype.map((item, idx) => [idx, { percent: item.exposure_perc }])) }}
        title={`Assettype Exposure ${titleSuffix}`}
        labelPrefix="Assettype"
        legendInformation={Object.fromEntries(data.Assettype.map((item, idx) => [idx, item.name]))}
        legendFontSize={12}
      />
    </>
  );

  const renderPortfolioPieCharts = () => {
    if (!portfolioData) return null;

    const {
      asset_type_division,
      industry_division,
      risk_division,
      pe_division,
      currency_division,
      category_userstatus,
      portfolio_topline,
      top_contributors,
    } = portfolioData;

    // Add 'Cash' to category_userstatus
    const cashPart = parseFloat(portfolio_topline[0]?.cash_eur_part_of_portfolio);
    let updatedCategoryUserStatus = [...category_userstatus];
    if (!isNaN(cashPart)) {
      updatedCategoryUserStatus.push({
        category: "Cash",
        part_of_portfolio: cashPart.toString(),
      });
    }

    return (
      <div className="grid-container">
        {top_contributors && (
          <Piecharts_MUI
            data={makeData(top_contributors, "column2_name")}
            title="Top Contributors to Unrealized Profit"
            labelPrefix="Contributor"
            legendInformation={makeLegend(top_contributors, "column1_name")}
            legendFontSize={12}
            chartHeight={300}
            maxWidth={400}
          />
        )}

        {updatedCategoryUserStatus && (
          <Piecharts_MUI
            data={makeData(updatedCategoryUserStatus, "part_of_portfolio")}
            title="Division per Category"
            labelPrefix="Category"
            legendInformation={makeLegend(updatedCategoryUserStatus, "category")}
            legendFontSize={12}
            chartHeight={300}
            maxWidth={400}
          />
        )}

        {pe_division && (
          <Piecharts_MUI
            data={makeData(pe_division, "column2_name")}
            title="PE Division"
            labelPrefix="PE"
            legendInformation={makeLegend(pe_division, "column1_name")}
            legendFontSize={12}
            chartHeight={300}
            maxWidth={400}
          />
        )}

        {portfolioData.fixed_return_components_over_asset_distribution?.distribution && (
          <Piecharts_MUI
            data={makeData(portfolioData.fixed_return_components_over_asset_distribution.distribution, "fixed_income")}
            title="Fixed Income Components"
            labelPrefix="Fixed"
            legendInformation={makeLegend(portfolioData.fixed_return_components_over_asset_distribution.distribution, "Assettype")}
            legendFontSize={12}
            chartHeight={300}
            maxWidth={400}
          />
        )}
      </div>
    );
  };

  // Risk analysis functions (from PF_Risk.jsx)
  const extractLegendInformation = (riskData) => {
    if (!riskData) return [];
    const userId = Object.keys(riskData)[0];
    const entries = riskData[userId];
  
    const filteredEntries = Object.entries(entries).filter(([key]) => key !== "unknown");
  
    return filteredEntries.map(([riskLevel, val]) => `Risk ${riskLevel}: ${val.assettypes}`);
  };

  // DAS-2538 / DAS-2537: colour bands for concentration + drawdown.
  const concColor = (l) => (l === 'Concentrated' ? '#c62828' : l === 'Moderate' ? '#f57c00' : '#2e7d32');
  const ddColor = (p) => (p < -20 ? '#c62828' : p < -10 ? '#f57c00' : '#2e7d32');

  const renderRiskAnalysis = () => {
    if (!riskData) return <p>No risk data available.</p>;

    const legendInfo = extractLegendInformation(riskData);
    const hasConc = concentration && concentration.hhi != null;
    const hasDD = drawdown && drawdown.max_drawdown_pct != null;

    return (
      <div>
        {(hasConc || hasDD) ? (
          <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', alignItems: 'baseline', margin: '0 0 12px', fontSize: 13 }}>
            {hasConc && (
              <>
                <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                  <strong>Top-{concentration.top_n} weight:</strong>
                  <InfoTooltip placement="top" text={`Share of your portfolio held in your ${concentration.top_n} largest positions. Calculated as the combined value of the ${concentration.top_n} biggest holdings divided by total portfolio value. Higher = more concentrated in a few names; shown amber above 60%.`} />
                  {' '}
                  <span style={{ color: concentration.top_n_weight_pct > 60 ? '#f57c00' : 'inherit', fontWeight: 600 }}>
                    {concentration.top_n_weight_pct != null ? `${concentration.top_n_weight_pct}%` : '—'}</span>
                </span>
                <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                  <strong>Concentration (HHI):</strong>
                  <InfoTooltip placement="top" text="Herfindahl-Hirschman Index — a single concentration score from 0 to 10,000. Calculated as the sum of each holding's squared weight × 10,000 (weight = holding value ÷ total portfolio). 10,000 = everything in one position; lower = more spread out. Bands: under 1,500 = Diversified, 1,500–2,500 = Moderate, above 2,500 = Concentrated." />
                  {' '}
                  <span style={{ color: concColor(concentration.label), fontWeight: 600 }}>
                    {concentration.hhi.toLocaleString()} {concentration.label}</span>
                </span>
              </>
            )}
            {hasDD && (
              <>
                <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                  <strong>Max drawdown:</strong>
                  <InfoTooltip placement="top" text="The worst peak-to-trough fall of your portfolio over the tracked window. Taken from the cumulative-return curve as the largest decline from a prior high. E.g. −7.5% means that, at its worst, the portfolio sat 7.5% below an earlier peak before recovering. (Less negative = milder worst-case loss.)" />
                  {' '}
                  <span style={{ color: ddColor(drawdown.max_drawdown_pct), fontWeight: 600 }}>
                    {drawdown.max_drawdown_pct.toFixed(1)}%</span>
                </span>
                <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                  <strong>Current (from peak):</strong>
                  <InfoTooltip placement="top" text="How far the portfolio is below its most recent high right now. Calculated as current cumulative value ÷ the running maximum − 1. Shows 'At/near peak' when within 0.5% of the high (i.e. not currently in a meaningful drawdown)." />
                  {' '}
                  <span style={{ fontWeight: 600 }}>
                    {drawdown.current_drawdown_pct == null
                      ? '—'
                      : drawdown.current_drawdown_pct < -0.5
                        ? `${drawdown.current_drawdown_pct.toFixed(1)}%`
                        : 'At/near peak'}</span>
                </span>
              </>
            )}
          </div>
        ) : null}
        <Piecharts_MUI
          data={riskData} 
          title="Risk Distribution" 
          labelPrefix="Risk" 
          legendInformation={legendInfo}
          legendFontSize={8}
        />
        <RiskCategory data={riskData} />
      </div>
    );
  };

  return (
    <>
      <Collapse defaultActiveKey={[]}>
        <Panel header="Portfolio Composition Charts" key="portfolio_charts">
          {renderPortfolioPieCharts()}
        </Panel>

        <Panel header="Risk Analysis" key="risk_analysis">
          {renderRiskAnalysis()}
        </Panel>

        <Panel header="Geographic & Currency Exposure (Filtered: no Cash, Real Estate, etc.)" key="overview_filtered">
          {exposureDataFiltered && renderExposurePieCharts(exposureDataFiltered, '(Filtered)')}
        </Panel>

        <Panel header="Geographic & Currency Exposure (Total: includes all asset types)" key="overview_total">
          {exposureDataTotal && renderExposurePieCharts(exposureDataTotal, '(Total)')}
        </Panel>

        <Panel header="Detailed Exposure Breakdown" key="details">
          <Table
            columns={columns}
            dataSource={dataSource}
            pagination={{ pageSize: 10 }}
          />
        </Panel>
      </Collapse>

      {isModalVisible && (
        <ThreeTablesModal
          tickerValue={selectedTicker}
          open={isModalVisible}
          onClose={() => setIsModalVisible(false)}
          userId={userId}
        />
      )}
    </>
  );
};

export default PF_Exposure;
